import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatErr } from "../lib/api";
import LanguageSelector from "../components/LanguageSelector";
import { Users, GraduationCap } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_project-sync-pro-1/artifacts/y56otaul_Virtually%20%281%29.png";

export default function Signup() {
    const { t } = useTranslation();
    const { signup } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "professional", signup_source: "", ref: "" });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    // Pick up ?ref=CODE from URL (or stashed value from a prior visit) so
    // links like /signup?ref=abc123 attribute the new user to their inviter.
    useEffect(() => {
        const qs = new URLSearchParams(window.location.search || "");
        const fromUrl = qs.get("ref");
        const fromStore = sessionStorage.getItem("vu_pending_ref");
        const code = (fromUrl || fromStore || "").trim();
        if (code) {
            setForm((f) => ({ ...f, ref: code }));
            sessionStorage.setItem("vu_pending_ref", code);
        }
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        try {
            await signup(form);
            nav("/dashboard");
        } catch (e2) {
            setErr(formatErr(e2.response?.data?.detail) || e2.message);
        } finally { setLoading(false); }
    };

    const google = () => {
        // OAuth callback must land on the SAME origin the user is browsing —
        // if we derived it from REACT_APP_BACKEND_URL instead, a user viewing
        // the app from a preview host / mirror / custom-domain would trigger
        // a cross-origin "Network Error" when the callback fires.
        // Pass role + signup_source as query params so they survive
        // cross-origin redirects (sessionStorage is scoped to origin and
        // can't cross the boundary).
        if (!form.signup_source) { setErr("Please tell us how you heard about us."); return; }
        const params = new URLSearchParams({
            role: form.role || "",
            source: form.signup_source,
        });
        if (form.ref) params.set("ref", form.ref);
        sessionStorage.setItem("vu_pending_role", form.role);
        sessionStorage.setItem("vu_pending_source", form.signup_source);
        if (form.ref) sessionStorage.setItem("vu_pending_ref", form.ref);
        const redirect = window.location.origin + "/auth/callback?" + params.toString();
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };

    const RoleCard = ({ value, label, desc, Icon }) => (
        <button type="button" onClick={() => setForm((f) => ({ ...f, role: value }))}
                data-testid={`role-${value}`}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                    form.role === value
                        ? "border-[var(--brand-purple)] bg-[rgba(123,45,142,0.06)]"
                        : "border-[rgba(26,19,24,0.12)] hover:border-[var(--brand-orange)]"
                }`}>
            <Icon size={18} className={form.role === value ? "text-[var(--brand-purple)]" : "text-[var(--text-ink)]/60"} />
            <div className="font-serif text-lg mt-1">{label}</div>
            <div className="text-xs text-[var(--text-ink)]/65">{desc}</div>
        </button>
    );

    return (
        <div className="min-h-screen grain grid md:grid-cols-2" data-testid="signup-page">
            <div className="hidden md:block relative">
                <img src="https://images.unsplash.com/photo-1763890965405-a376a73dc8ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                     alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[var(--brand-orange)]/55 via-[var(--brand-magenta)]/35 to-[var(--brand-purple)]/55 mix-blend-multiply" />
                <div className="relative z-[2] h-full flex flex-col justify-end p-10 text-white">
                    <div className="font-script text-3xl">begin again</div>
                    <div className="font-serif text-4xl leading-tight">Become <em>unstoppable.</em></div>
                </div>
            </div>
            <div className="flex items-center justify-center p-6 md:p-10 relative z-[2]">
                <form onSubmit={submit} className="w-full max-w-md card-warm p-7 fade-up" data-testid="signup-form">
                    <div className="flex items-start justify-between gap-3 mb-5">
                        <div className="flex items-center gap-3">
                            <img src={LOGO} alt="" className="h-10 w-10 object-contain" />
                            <div>
                                <div className="font-serif text-lg">{t("auth.signupTitle")}</div>
                                <div className="text-xs text-[var(--text-ink)]/60">{t("auth.signupSubtitle")}</div>
                            </div>
                        </div>
                        <LanguageSelector compact testid="signup-language-selector" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <RoleCard value="professional" label={t("auth.rolePro")} desc="Clients, contracts, invoicing" Icon={Users} />
                        <RoleCard value="student" label={t("auth.roleStudent")} desc="Classes, references, meal plans" Icon={GraduationCap} />
                    </div>
                    <button type="button" onClick={google} data-testid="signup-google-btn"
                            className="w-full btn-ghost flex items-center justify-center gap-2 mb-3">
                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4" />
                        {t("auth.withGoogle")}
                    </button>
                    <div className="my-3 flex items-center gap-3 text-xs text-[var(--text-ink)]/50">
                        <div className="flex-1 h-px bg-[rgba(26,19,24,0.12)]" /> {t("auth.or")} <div className="flex-1 h-px bg-[rgba(26,19,24,0.12)]" />
                    </div>
                    <label className="text-sm">{t("auth.fullName")}</label>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                           data-testid="signup-name" className="w-full mt-1 mb-2 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] focus:outline-none focus:border-[var(--brand-purple)]" />
                    <label className="text-sm">{t("auth.email")}</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                           data-testid="signup-email" className="w-full mt-1 mb-2 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] focus:outline-none focus:border-[var(--brand-purple)]" />
                    <label className="text-sm">{t("auth.password")}</label>
                    <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                           data-testid="signup-password" className="w-full mt-1 mb-2 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] focus:outline-none focus:border-[var(--brand-purple)]" />
                    <label className="text-sm">How did you hear about us?</label>
                    <select required value={form.signup_source} onChange={(e) => setForm({ ...form, signup_source: e.target.value })}
                            data-testid="signup-source"
                            className="w-full mt-1 mb-2 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] focus:outline-none focus:border-[var(--brand-purple)] bg-white">
                        <option value="" disabled>Select an option…</option>
                        <option value="TikTok">TikTok</option>
                        <option value="Instagram">Instagram</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Friend/Referral">Friend / Referral</option>
                        <option value="University/Society">University / Society</option>
                        <option value="Creator/Partner">Creator / Partner</option>
                        <option value="Other">Other</option>
                    </select>                    {err && <div className="text-sm text-[var(--status-red)]" data-testid="signup-error">{err}</div>}
                    <button disabled={loading} type="submit" data-testid="signup-submit-btn"
                            className="btn-primary w-full mt-4">{loading ? t("auth.creating") : t("auth.signUp")}</button>
                    <div className="text-sm mt-3 text-center">
                        {t("auth.haveAccount")} <Link to="/login" data-testid="signup-login-link" className="text-[var(--brand-purple)] hover:underline">{t("auth.signIn")}</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
